import { CheckIcon, ImageIcon, XIcon } from "lucide-react";
import type { TFunction } from "i18next";

import { Card, CardContent } from "@/components/ui/card";

interface DynamicField {
  title: string;
  value: unknown;
}

interface EvaluationFormData {
  image?: string;
  controlDate?: string;
  dynamicFields?: DynamicField[];
}

interface EvaluationFormCardProps {
  t: TFunction;
  data: EvaluationFormData | null | undefined;
  onSee: (images: string[]) => void;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export default function EvaluationFormCard({
  t,
  data,
  onSee,
}: EvaluationFormCardProps) {
  return (
    <Card>
      <CardContent>
        <dl className="space-y-4">
          <Row label={`${t("inspections.evaluation-form-card.image")}:`}>
            {data?.image ? (
              <button
                type="button"
                onClick={() => onSee([data.image as string])}
                className="ml-2 overflow-hidden rounded-md"
                aria-label={t(
                  "inspections.see-inspection.image-open",
                  "Open image"
                )}
              >
                <img
                  src={data.image}
                  alt=""
                  className="size-12 cursor-pointer object-cover"
                />
              </button>
            ) : (
              <ImageIcon className="ml-2 size-7 text-muted-foreground" />
            )}
          </Row>
          <Row label={`${t("inspections.evaluation-form-card.date")}:`}>
            {data?.controlDate}
          </Row>
          {data?.dynamicFields?.map((field) => (
            <Row key={field.title} label={`${field.title}:`}>
              {typeof field.value === "boolean" ? (
                field.value ? (
                  <CheckIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <XIcon className="size-4 text-destructive" />
                )
              ) : (
                <span>{String(field.value ?? "")}</span>
              )}
            </Row>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
